// src/pages/Dashboard.js
import { useState, useEffect, useCallback } from 'react';
import { Link, useOutletContext } from 'react-router-dom';
import { collection, query, where, addDoc, serverTimestamp, orderBy, getDocs, Timestamp, doc, deleteDoc, updateDoc, limit, startAfter } from 'firebase/firestore';
import { db } from '../firebase';
import QRCode from 'qrcode';
import { useToast } from '../components/ToastProvider';
import { logAudit } from '../utils/auditLog';

// --- [IMPORTANT] Add your admin User IDs here ---
// --- [สำคัญ] เพิ่ม UID ของผู้ดูแลระบบที่นี่ ---
const ADMIN_UIDS = [
    'cXXmwFwOebRnN0h6rPb1E4ASRIt1',
    'WosYCVaIbrXGFLST7sjEQc1WQaU2',
];

// --- SVG Icon Components ---
const DownloadIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
    </svg>
);

const EyeIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
        <path strokeLinecap="round" strokeLinejoin="round" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
    </svg>
);

const CopyIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
    </svg>
);

const TrashIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
    </svg>
);

const EditIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.536L15.232 5.232z" />
    </svg>
);

const SearchIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
    </svg>
);

const AtSymbolIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M16 12a4 4 0 10-8 0 4 4 0 008 0zm0 0v1.5a2.5 2.5 0 005 0V12a9 9 0 10-9 9m4.5-1.206a8.959 8.959 0 01-4.5 1.207" />
    </svg>
);

const ShareIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M8.684 13.342C8.886 12.938 9 12.482 9 12s-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6.002l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.368a3 3 0 105.367 2.684 3 3 0 00-5.367-2.684z" />
    </svg>
);

const ChevronLeftIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
    </svg>
);

const ChevronRightIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
    </svg>
);

// --- [ADDED] Icon for Report Button ---
const ChartBarIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
    </svg>
);


// --- Share Button Component ---
const ShareButton = ({ shortUrl, longUrl, onCopy }) => {
    const handleShare = async () => {
        const shareData = {
            title: 'แชร์ลิงก์ย่อ',
            text: `ลิงก์สำหรับ: ${longUrl}`,
            url: shortUrl,
        };

        if (navigator.share) {
            try {
                await navigator.share(shareData);
            } catch (err) {
                // User cancelled the share action, do nothing.
            }
        } else {
            onCopy(shortUrl);
        }
    };

    return (
        <button
            onClick={handleShare}
            className="p-1 text-slate-400 hover:text-green-700 rounded-full hover:bg-green-100 transition-colors"
            title="แชร์ลิงก์"
        >
            <ShareIcon />
        </button>
    );
};


// --- Confirmation Modal Component ---
const ConfirmDeleteModal = ({ isOpen, onClose, onConfirm, linkShortCode }) => {
    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50">
            <div className="bg-white p-6 rounded-lg shadow-xl max-w-sm w-full">
                <h3 className="text-lg font-bold text-gray-900">ยืนยันการลบ</h3>
                <p className="mt-2 text-sm text-gray-600">
                    คุณแน่ใจหรือไม่ว่าต้องการลบลิงก์นี้?
                    <br />
                    <strong className="font-mono">{linkShortCode}</strong>
                    <br />
                    การกระทำนี้ไม่สามารถย้อนกลับได้
                </p>
                <div className="mt-4 flex justify-end gap-3">
                    <button onClick={onClose} className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200">
                        ยกเลิก
                    </button>
                    <button onClick={onConfirm} className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700">
                        ยืนยันการลบ
                    </button>
                </div>
            </div>
        </div>
    );
};

// --- Edit Modal Component ---
const EditLinkModal = ({ isOpen, onClose, onSave, linkData }) => {
    const [formData, setFormData] = useState({});

    useEffect(() => {
        if (linkData) {
            const expiresDate = linkData.expiresAt ? new Date(linkData.expiresAt.seconds * 1000).toISOString().slice(0, 16) : '';
            setFormData({
                longUrl: linkData.longUrl || '',
                password: linkData.password || '',
                expiresAt: expiresDate,
                maxClicks: linkData.maxClicks || '',
                tags: (linkData.tags || []).join(', ')
            });
        }
    }, [linkData]);

    if (!isOpen) return null;

    const handleChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    const handleSave = () => {
        onSave(linkData.id, formData);
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50">
            <div className="bg-white p-6 rounded-lg shadow-xl max-w-lg w-full">
                <h3 className="text-lg font-bold text-gray-900">แก้ไขลิงก์</h3>
                <div className="mt-4 space-y-4">
                    <div>
                        <label htmlFor="edit-longUrl" className="block text-sm font-medium text-gray-700">URL ปลายทาง</label>
                        <input type="url" name="longUrl" id="edit-longUrl" value={formData.longUrl} onChange={handleChange} className="mt-1 block w-full rounded-md border-gray-300 shadow-sm p-2" />
                    </div>
                    <div>
                        <label htmlFor="edit-password" className="block text-sm font-medium text-gray-700">รหัสผ่าน (ไม่จำเป็น)</label>
                        <input type="text" name="password" id="edit-password" value={formData.password} onChange={handleChange} className="mt-1 block w-full rounded-md border-gray-300 shadow-sm p-2" />
                    </div>
                    <div>
                        <label htmlFor="edit-expiresAt" className="block text-sm font-medium text-gray-700">วันหมดอายุ (ไม่จำเป็น)</label>
                        <input type="datetime-local" name="expiresAt" id="edit-expiresAt" value={formData.expiresAt} onChange={handleChange} className="mt-1 block w-full rounded-md border-gray-300 shadow-sm p-2" />
                    </div>
                    <div>
                        <label htmlFor="edit-maxClicks" className="block text-sm font-medium text-gray-700">จำกัดจำนวนคลิก (0 = ไม่จำกัด)</label>
                        <input type="number" name="maxClicks" id="edit-maxClicks" value={formData.maxClicks} onChange={handleChange} min="0" className="mt-1 block w-full rounded-md border-gray-300 shadow-sm p-2" />
                    </div>
                    <div>
                        <label htmlFor="edit-tags" className="block text-sm font-medium text-gray-700">ป้ายกำกับ (คั่นด้วยลูกน้ำ ,)</label>
                        <input type="text" name="tags" id="edit-tags" value={formData.tags} onChange={handleChange} className="mt-1 block w-full rounded-md border-gray-300 shadow-sm p-2" />
                    </div>
                </div>
                <div className="mt-6 flex justify-end gap-3">
                    <button onClick={onClose} className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200">
                        ยกเลิก
                    </button>
                    <button onClick={handleSave} className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700">
                        บันทึกการเปลี่ยนแปลง
                    </button>
                </div>
            </div>
        </div>
    );
};

// --- Modal for editing Custom URL ---
const EditCustomUrlModal = ({ isOpen, onClose, onSave, linkData }) => {
    const [newCustomCode, setNewCustomCode] = useState('');
    const [validationStatus, setValidationStatus] = useState('idle');
    const [validationError, setValidationError] = useState('');
    
    useEffect(() => {
        if (linkData) {
            setNewCustomCode(linkData.shortCode);
        }
    }, [linkData]);

    useEffect(() => {
        const handler = setTimeout(async () => {
            const code = newCustomCode.trim();

            if (!code) {
                setValidationStatus('idle');
                setValidationError('');
                return;
            }
            if (code === linkData?.shortCode) {
                setValidationStatus('available');
                setValidationError('');
                return;
            }

            if (!/^[a-zA-Z0-9_-]+$/.test(code)) {
                setValidationStatus('invalid');
                setValidationError('ใช้ได้เฉพาะตัวอักษร, ตัวเลข, - และ _');
                return;
            }

            setValidationStatus('checking');
            setValidationError('');

            try {
                const q = query(collection(db, "urls"), where("shortCode", "==", code));
                const querySnapshot = await getDocs(q);
                if (querySnapshot.empty) {
                    setValidationStatus('available');
                } else {
                    setValidationStatus('taken');
                    setValidationError('ชื่อนี้มีผู้ใช้งานแล้ว');
                }
            } catch (err) {
                console.error("Error checking custom code:", err);
                setValidationStatus('idle');
            }

        }, 500);

        return () => clearTimeout(handler);
    }, [newCustomCode, linkData]);

    if (!isOpen || !linkData) return null;

    const handleSave = () => {
        if (validationStatus === 'available') {
            onSave(linkData.id, newCustomCode.trim());
        }
    };

    const isSaveDisabled = validationStatus !== 'available';

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50">
            <div className="bg-white p-6 rounded-lg shadow-xl max-w-lg w-full">
                <h3 className="text-lg font-bold text-gray-900">แก้ไข Custom URL</h3>
                
                <div className="mt-4 p-4 bg-red-50 border-l-4 border-red-500 text-red-700">
                    <p className="font-bold">คำเตือน</p>
                    <p>การเปลี่ยน URL จะทำให้ลิงก์เดิมที่เคยแชร์ไปแล้วใช้งานไม่ได้</p>
                </div>

                <div className="mt-4">
                    <label htmlFor="edit-customUrl" className="block text-sm font-medium text-gray-700">
                        Custom URL ใหม่
                    </label>
                    <div className="flex items-center mt-1">
                        <span className="inline-flex items-center px-3 rounded-l-md border border-r-0 border-gray-300 bg-gray-50 text-gray-500 text-sm">
                            {window.location.host}/
                        </span>
                        <input 
                            type="text" 
                            id="edit-customUrl" 
                            value={newCustomCode} 
                            onChange={e => setNewCustomCode(e.target.value)} 
                            className="flex-1 block w-full rounded-none rounded-r-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm p-2"
                        />
                    </div>
                    <div className="mt-1 text-sm h-5">
                        {validationStatus === 'checking' && <p className="text-gray-500">กำลังตรวจสอบ...</p>}
                        {validationStatus === 'available' && <p className="text-green-600 font-semibold">สามารถใช้งานได้!</p>}
                        {validationStatus === 'taken' && <p className="text-red-500">{validationError}</p>}
                        {validationStatus === 'invalid' && <p className="text-red-500">{validationError}</p>}
                    </div>
                </div>

                <div className="mt-6 flex justify-end gap-3">
                    <button onClick={onClose} className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200">
                        ยกเลิก
                    </button>
                    <button 
                        onClick={handleSave} 
                        disabled={isSaveDisabled}
                        className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:bg-blue-300 disabled:cursor-not-allowed"
                    >
                        บันทึก URL ใหม่
                    </button>
                </div>
            </div>
        </div>
    );
};


const Dashboard = () => {
    const { user } = useOutletContext();
    const { showToast } = useToast();
    const [longUrl, setLongUrl] = useState('');
    const [shortenedUrls, setShortenedUrls] = useState([]);
    const [error, setError] = useState('');
    const [, setNewlyCreatedUrl] = useState(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [visiblePasswords, setVisiblePasswords] = useState({});
    const [copySuccess, setCopySuccess] = useState('');
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [linkToDelete, setLinkToDelete] = useState(null);
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [linkToEdit, setLinkToEdit] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');

    const [isCustomUrlModalOpen, setIsCustomUrlModalOpen] = useState(false);
    const [linkToEditCustomUrl, setLinkToEditCustomUrl] = useState(null);

    const [isCustomUrlVisible, setIsCustomUrlVisible] = useState(false);
    const [isAdvancedOptionsVisible, setIsAdvancedOptionsVisible] = useState(false);
    
    const [customShortCode, setCustomShortCode] = useState('');
    const [customCodeStatus, setCustomCodeStatus] = useState('idle');
    const [customCodeError, setCustomCodeError] = useState('');
    const [expiresAt, setExpiresAt] = useState('');
    const [maxClicks, setMaxClicks] = useState('');
    const [password, setPassword] = useState('');
    const [tags, setTags] = useState('');

    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 10;
    const [isPageLoading, setIsPageLoading] = useState(false);
    const [hasMorePages, setHasMorePages] = useState(false);
    const [pageCursors, setPageCursors] = useState({ 1: null });
    const [lastVisibleDoc, setLastVisibleDoc] = useState(null);

    const [isAdmin, setIsAdmin] = useState(false);
    const [viewMode, setViewMode] = useState('myLinks');

    useEffect(() => {
        if (user) {
            setIsAdmin(ADMIN_UIDS.includes(user.uid));
        }
    }, [user]);

    const loadPage = useCallback(async (pageNumber, cursorDoc = null) => {
        if (!user) return;

        setIsPageLoading(true);
        setError('');

        try {
            const constraints = [];

            if (isAdmin && viewMode === 'allLinks') {
                constraints.push(orderBy("createdAt", "desc"));
            } else {
                constraints.push(where("uid", "==", user.uid), orderBy("createdAt", "desc"));
            }

            if (cursorDoc) {
                constraints.push(startAfter(cursorDoc));
            }

            constraints.push(limit(itemsPerPage + 1));

            const pageQuery = query(collection(db, "urls"), ...constraints);
            const snapshot = await getDocs(pageQuery);

            const docs = snapshot.docs;
            const hasNext = docs.length > itemsPerPage;
            const pageDocs = hasNext ? docs.slice(0, itemsPerPage) : docs;
            const nextCursorDoc = pageDocs.length > 0 ? pageDocs[pageDocs.length - 1] : null;

            setHasMorePages(hasNext);
            setShortenedUrls(pageDocs.map((docItem) => ({ id: docItem.id, ...docItem.data() })));
            setCurrentPage(pageNumber);
            setLastVisibleDoc(nextCursorDoc);

            setPageCursors((prev) => {
                const next = { ...prev, [pageNumber]: cursorDoc || null };
                if (hasNext && nextCursorDoc) {
                    next[pageNumber + 1] = nextCursorDoc;
                }
                return next;
            });
        } catch (err) {
            console.error("Error fetching paged data: ", err);
            setError("ไม่สามารถดึงข้อมูลล่าสุดได้");
        } finally {
            setIsPageLoading(false);
        }
    }, [isAdmin, viewMode, user, itemsPerPage]);

    useEffect(() => {
        if (!user) {
            setShortenedUrls([]);
            setHasMorePages(false);
            setPageCursors({ 1: null });
            setCurrentPage(1);
            setLastVisibleDoc(null);
            return;
        }

        setPageCursors({ 1: null });
        setLastVisibleDoc(null);
        loadPage(1, null);
    }, [user, loadPage]);

    useEffect(() => {
        if (!isCustomUrlVisible) {
            setCustomCodeStatus('idle');
            setCustomCodeError('');
            return;
        }

        const handler = setTimeout(async () => {
            const code = customShortCode.trim();

            if (!code) {
                setCustomCodeStatus('idle');
                setCustomCodeError('');
                return;
            }

            if (!/^[a-zA-Z0-9_-]+$/.test(code)) {
                setCustomCodeStatus('invalid');
                setCustomCodeError('ใช้ได้เฉพาะตัวอักษร, ตัวเลข, - และ _');
                return;
            }

            setCustomCodeStatus('checking');
            setCustomCodeError('');

            try {
                const q = query(collection(db, "urls"), where("shortCode", "==", code));
                const querySnapshot = await getDocs(q);
                if (querySnapshot.empty) {
                    setCustomCodeStatus('available');
                } else {
                    setCustomCodeStatus('taken');
                    setCustomCodeError('ชื่อนี้มีผู้ใช้งานแล้ว');
                }
            } catch (err) {
                console.error("Error checking custom code:", err);
                setCustomCodeStatus('idle');
            }

        }, 500);

        return () => clearTimeout(handler);
    }, [customShortCode, isCustomUrlVisible]);

    const generateShortCode = async () => {
        const chars = 'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
        let code = '';
        for (let i = 0; i < 6; i++) {
            code += chars.charAt(Math.floor(Math.random() * chars.length));
        }
        const q = query(collection(db, "urls"), where("shortCode", "==", code));
        const querySnapshot = await getDocs(q);
        return querySnapshot.empty ? code : generateShortCode();
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');

        if (!longUrl) {
            setError('กรุณาใส่ URL ที่ต้องการย่อ');
            return;
        }
        try { new URL(longUrl); } catch (_) {
            setError('รูปแบบ URL ไม่ถูกต้อง');
            return;
        }
        
        if (isCustomUrlVisible && customShortCode) {
            if (customCodeStatus === 'taken' || customCodeStatus === 'invalid') {
                setError('กรุณาแก้ไข Custom URL ให้ถูกต้อง');
                return;
            }
            if (customCodeStatus === 'checking') {
                setError('กรุณารอการตรวจสอบ Custom URL สักครู่');
                return;
            }
        }

        setIsSubmitting(true);
        setNewlyCreatedUrl(null);

        let finalShortCode = '';
        if (isCustomUrlVisible && customShortCode && customCodeStatus === 'available') {
            finalShortCode = customShortCode.trim();
        } else {
            finalShortCode = await generateShortCode();
        }
        
        const newUrlData = {
            uid: user.uid,
            creatorEmail: user.email,
            longUrl,
            shortCode: finalShortCode,
            createdAt: serverTimestamp(),
            clickCount: 0,
        };

        if (isAdvancedOptionsVisible) {
            if (expiresAt) newUrlData.expiresAt = Timestamp.fromDate(new Date(expiresAt));
            if (maxClicks && Number(maxClicks) > 0) newUrlData.maxClicks = Number(maxClicks);
            if (password) newUrlData.password = password;
        }
        if ((isAdvancedOptionsVisible || isCustomUrlVisible) && tags) {
             newUrlData.tags = tags.split(',').map(tag => tag.trim()).filter(Boolean);
        }


        try {
            await addDoc(collection(db, "urls"), newUrlData);
            
            const baseUrl = window.location.origin;
            const tempSuccessMessage = {
                longUrl,
                shortUrl: `${baseUrl}/${newUrlData.shortCode}`,
                shortCode: newUrlData.shortCode,
            };
            setNewlyCreatedUrl(tempSuccessMessage);
            
            setLongUrl('');
            setCustomShortCode('');
            setExpiresAt('');
            setMaxClicks('');
            setPassword('');
            setTags('');
            setIsCustomUrlVisible(false);
            setIsAdvancedOptionsVisible(false);

        } catch (error) {
            setError("เกิดข้อผิดพลาดในการสร้างลิงก์ย่อ");
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleDownloadQR = async (shortCode) => {
        const baseUrl = window.location.origin;
        const urlToEncode = `${baseUrl}/${shortCode}`;
        try {
            const dataUrl = await QRCode.toDataURL(urlToEncode, {
                errorCorrectionLevel: 'H',
                margin: 4,
                width: 300,
                color: { dark: '#000000', light: '#FFFFFF' }
            });
            const a = document.createElement('a');
            a.href = dataUrl;
            a.download = `qr-code-${shortCode}.png`;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
        } catch (err) {
            console.error('Failed to generate QR Code for download', err);
            showToast('ไม่สามารถสร้าง QR Code ได้', 'error');
        }
    };

    const togglePasswordVisibility = (linkId) => {
        setVisiblePasswords(prevState => ({
            ...prevState,
            [linkId]: !prevState[linkId]
        }));
    };

    const handleCopy = (text) => {
        navigator.clipboard.writeText(text).then(() => {
            setCopySuccess(text);
            setTimeout(() => setCopySuccess(''), 2000);
        }, (err) => {
            console.error('Failed to copy text: ', err);
            showToast('ไม่สามารถคัดลอกลิงก์ได้', 'error');
        });
    };

    const handleDeleteClick = (link) => {
        setLinkToDelete(link);
        setIsDeleteModalOpen(true);
    };

    const confirmDelete = async () => {
        if (linkToDelete) {
            try {
                await deleteDoc(doc(db, "urls", linkToDelete.id));
                await logAudit({
                    action: 'DELETE_LINK',
                    actorUid: user.uid,
                    actorEmail: user.email,
                    targetId: linkToDelete.id,
                    metadata: { shortCode: linkToDelete.shortCode || '' },
                });
                showToast('ลบลิงก์สำเร็จ', 'success');
            } catch (error) {
                console.error("Error deleting document: ", error);
                showToast('เกิดข้อผิดพลาดในการลบลิงก์', 'error');
            } finally {
                setLinkToDelete(null);
                setIsDeleteModalOpen(false);
            }
        }
    };

    const handleEditClick = (link) => {
        setLinkToEdit(link);
        setIsEditModalOpen(true);
    };

    const handleUpdateLink = async (linkId, updatedData) => {
        try {
            const linkRef = doc(db, "urls", linkId);
            const dataToUpdate = {
                longUrl: updatedData.longUrl,
                password: updatedData.password || null,
                expiresAt: updatedData.expiresAt ? Timestamp.fromDate(new Date(updatedData.expiresAt)) : null,
                maxClicks: Number(updatedData.maxClicks) || 0,
                tags: updatedData.tags.split(',').map(tag => tag.trim()).filter(Boolean)
            };
            await updateDoc(linkRef, dataToUpdate);
            await logAudit({
                action: 'UPDATE_LINK',
                actorUid: user.uid,
                actorEmail: user.email,
                targetId: linkId,
                metadata: {
                    hasPassword: Boolean(dataToUpdate.password),
                    maxClicks: dataToUpdate.maxClicks,
                    tagsCount: dataToUpdate.tags.length,
                },
            });
            showToast('อัปเดตลิงก์สำเร็จ', 'success');
        } catch (error) {
            console.error("Error updating document: ", error);
            showToast('เกิดข้อผิดพลาดในการอัปเดตลิงก์', 'error');
        } finally {
            setIsEditModalOpen(false);
            setLinkToEdit(null);
        }
    };
    
    const handleCustomUrlEditClick = (link) => {
        setLinkToEditCustomUrl(link);
        setIsCustomUrlModalOpen(true);
    };

    const handleUpdateCustomUrl = async (linkId, newShortCode) => {
        try {
            const linkRef = doc(db, "urls", linkId);
            await updateDoc(linkRef, { shortCode: newShortCode });
            await logAudit({
                action: 'UPDATE_SHORT_CODE',
                actorUid: user.uid,
                actorEmail: user.email,
                targetId: linkId,
                metadata: { newShortCode },
            });
            showToast('อัปเดต Custom URL สำเร็จ', 'success');
        } catch (error) {
            console.error("Error updating custom URL: ", error);
            showToast('เกิดข้อผิดพลาดในการอัปเดต Custom URL', 'error');
        } finally {
            setIsCustomUrlModalOpen(false);
            setLinkToEditCustomUrl(null);
        }
    };

    const filteredUrls = shortenedUrls.filter(url => {
        const searchTermLower = searchTerm.toLowerCase();
        if (!searchTerm) return true;

        const inLongUrl = url.longUrl.toLowerCase().includes(searchTermLower);
        const inShortCode = url.shortCode.toLowerCase().includes(searchTermLower);
        const inTags = url.tags?.some(tag => tag.toLowerCase().includes(searchTermLower));
        const inCreator = url.creatorEmail?.toLowerCase().includes(searchTermLower);

        return inLongUrl || inShortCode || inTags || (isAdmin && viewMode === 'allLinks' && inCreator);
    });

    const currentUrls = filteredUrls;

    const handleNextPage = () => {
        if (!hasMorePages || isPageLoading) return;
        const nextPage = currentPage + 1;
        const nextCursor = pageCursors[nextPage] || lastVisibleDoc || null;
        loadPage(nextPage, nextCursor);
    };

    const handlePrevPage = () => {
        if (currentPage <= 1 || isPageLoading) return;
        const prevPage = currentPage - 1;
        const prevCursor = pageCursors[prevPage] || null;
        loadPage(prevPage, prevCursor);
    };

    const knownPages = Object.keys(pageCursors)
        .map((value) => Number(value))
        .filter((value) => Number.isFinite(value) && value > 0);
    if (!knownPages.includes(currentPage)) knownPages.push(currentPage);
    if (hasMorePages && !knownPages.includes(currentPage + 1)) knownPages.push(currentPage + 1);
    const visiblePageNumbers = Array.from(new Set(knownPages))
        .sort((a, b) => a - b)
        .filter((value) => value >= Math.max(1, currentPage - 3) && value <= currentPage + 3);

    const handleGotoPage = (targetPage) => {
        if (targetPage === currentPage || isPageLoading) return;
        if (targetPage === currentPage + 1) {
            handleNextPage();
            return;
        }
        if (targetPage === currentPage - 1) {
            handlePrevPage();
            return;
        }
        if (!(targetPage in pageCursors)) return;
        loadPage(targetPage, pageCursors[targetPage] || null);
    };


    return (
        <main className="container mx-auto p-4 sm:p-6 lg:p-8">
            <ConfirmDeleteModal 
                isOpen={isDeleteModalOpen}
                onClose={() => setIsDeleteModalOpen(false)}
                onConfirm={confirmDelete}
                linkShortCode={linkToDelete?.shortCode}
            />
            <EditLinkModal
                isOpen={isEditModalOpen}
                onClose={() => setIsEditModalOpen(false)}
                onSave={handleUpdateLink}
                linkData={linkToEdit}
            />
            <EditCustomUrlModal
                isOpen={isCustomUrlModalOpen}
                onClose={() => setIsCustomUrlModalOpen(false)}
                onSave={handleUpdateCustomUrl}
                linkData={linkToEditCustomUrl}
            />
            <div className="bg-white p-6 rounded-xl shadow-lg">
                <h2 className="text-2xl font-bold text-gray-800 mb-4">ย่อลิงก์ของคุณ</h2>
                <form onSubmit={handleSubmit}>
                    <div className="flex flex-col sm:flex-row gap-2">
                        <input
                            type="url"
                            value={longUrl}
                            onChange={(e) => setLongUrl(e.target.value)}
                            placeholder="https://your-long-url.com/very/long/path"
                            className="flex-grow p-3 border-2 border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                            required
                        />
                        <button type="submit" disabled={isSubmitting} className="bg-blue-600 text-white font-bold py-3 px-6 rounded-lg hover:bg-blue-700 disabled:bg-blue-300">
                            {isSubmitting ? 'กำลังสร้าง...' : 'ย่อลิงก์'}
                        </button>
                    </div>
                    {error && <p className="text-red-500 mt-2">{error}</p>}
                    
                    <div className="mt-4 flex items-center gap-4">
                        <button
                            type="button"
                            onClick={() => setIsCustomUrlVisible(!isCustomUrlVisible)}
                            className={`px-4 py-2 rounded-lg text-sm font-semibold transition-colors ${
                                isCustomUrlVisible 
                                ? 'bg-blue-100 text-blue-800' 
                                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                            }`}
                        >
                            กำหนด URL เอง
                        </button>
                        <button
                            type="button"
                            onClick={() => setIsAdvancedOptionsVisible(!isAdvancedOptionsVisible)}
                            className={`px-4 py-2 rounded-lg text-sm font-semibold transition-colors ${
                                isAdvancedOptionsVisible
                                ? 'bg-blue-100 text-blue-800'
                                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                            }`}
                        >
                            ตัวเลือกเพิ่มเติม
                        </button>
                    </div>

                    {(isCustomUrlVisible || isAdvancedOptionsVisible) && (
                        <div className="mt-4 border-t border-gray-200 pt-4">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-6">
                                
                                {isCustomUrlVisible && (
                                    <div className="md:col-span-2">
                                        <label htmlFor="customShortCode" className="block text-sm font-medium text-gray-700">Custom URL</label>
                                        <div className="flex items-center mt-1">
                                            <span className="inline-flex items-center px-3 rounded-l-md border border-r-0 border-gray-300 bg-gray-50 text-gray-500 text-sm">
                                                {window.location.host}/
                                            </span>
                                            <input type="text" id="customShortCode" value={customShortCode} onChange={e => setCustomShortCode(e.target.value)} placeholder="my-custom-link" className="flex-1 block w-full rounded-none rounded-r-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm p-2"/>
                                        </div>
                                        <div className="mt-1 text-sm h-5">
                                            {customCodeStatus === 'checking' && <p className="text-gray-500">กำลังตรวจสอบ...</p>}
                                            {customCodeStatus === 'available' && <p className="text-green-600 font-semibold">สามารถใช้งานได้!</p>}
                                            {customCodeStatus === 'taken' && <p className="text-red-500">{customCodeError}</p>}
                                            {customCodeStatus === 'invalid' && <p className="text-red-500">{customCodeError}</p>}
                                        </div>
                                    </div>
                                )}

                                {isAdvancedOptionsVisible && (
                                    <>
                                        <div>
                                            <label htmlFor="password" className="block text-sm font-medium text-gray-700">รหัสผ่าน (ไม่จำเป็น)</label>
                                            <input type="password" id="password" value={password} onChange={e => setPassword(e.target.value)} className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm p-2"/>
                                        </div>
                                        <div>
                                            <label htmlFor="expiresAt" className="block text-sm font-medium text-gray-700">วันหมดอายุ (ไม่จำเป็น)</label>
                                            <input type="datetime-local" id="expiresAt" value={expiresAt} onChange={e => setExpiresAt(e.target.value)} className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm p-2"/>
                                        </div>
                                        <div>
                                            <label htmlFor="maxClicks" className="block text-sm font-medium text-gray-700">จำกัดจำนวนคลิก (0 = ไม่จำกัด)</label>
                                            <input type="number" id="maxClicks" value={maxClicks} onChange={e => setMaxClicks(e.target.value)} min="0" className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm p-2"/>
                                        </div>
                                    </>
                                )}
                                
                                <div className="md:col-span-2">
                                    <label htmlFor="tags" className="block text-sm font-medium text-gray-700">ป้ายกำกับ (คั่นด้วยลูกน้ำ ,)</label>
                                    <input type="text" id="tags" value={tags} onChange={e => setTags(e.target.value)} placeholder="เช่น งาน, ส่วนตัว" className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm p-2"/>
                                </div>

                            </div>
                        </div>
                    )}
                </form>
            </div>

             <div className="mt-8">
                <div className="flex justify-between items-center mb-4 flex-wrap gap-4">
                    <h2 className="text-2xl font-bold text-gray-800">
                        {isAdmin && viewMode === 'allLinks' ? 'ลิงก์ทั้งหมดในระบบ' : 'ประวัติลิงก์ของคุณ'}
                    </h2>
                    <div className="flex items-center gap-4 flex-wrap">
                        {/* --- [MODIFIED] Added Report Button for Admins --- */}
                        {isAdmin && (
                            <div className="flex items-center gap-2 p-1 bg-gray-200 rounded-lg">
                                <button onClick={() => setViewMode('myLinks')} className={`px-3 py-1 text-sm font-semibold rounded-md transition-colors ${viewMode === 'myLinks' ? 'bg-white shadow' : 'bg-transparent text-gray-600'}`}>
                                    ลิงก์ของฉัน
                                </button>
                                <button onClick={() => setViewMode('allLinks')} className={`px-3 py-1 text-sm font-semibold rounded-md transition-colors ${viewMode === 'allLinks' ? 'bg-white shadow' : 'bg-transparent text-gray-600'}`}>
                                    ลิงก์ทั้งหมด
                                </button>
                            </div>
                        )}
                        {isAdmin && (
                             <Link to="/admin-report" className="flex items-center px-3 py-1.5 text-sm font-semibold rounded-lg bg-blue-100 text-blue-800 hover:bg-blue-200 transition-colors">
                                <ChartBarIcon />
                                <span>ดูรายงาน</span>
                            </Link>
                        )}
                        {isAdmin && (
                             <Link to="/audit-log" className="flex items-center px-3 py-1.5 text-sm font-semibold rounded-lg bg-emerald-100 text-emerald-800 hover:bg-emerald-200 transition-colors">
                                <span>Audit Log</span>
                            </Link>
                        )}
                        <div className="relative">
                            <span className="absolute inset-y-0 left-0 flex items-center pl-3">
                                <SearchIcon />
                            </span>
                            <input
                                type="text"
                                placeholder="ค้นหา..."
                                value={searchTerm}
                                onChange={(e) => {
                                    setSearchTerm(e.target.value);
                                    setCurrentPage(1);
                                }}
                                className="pl-10 pr-4 py-2 border-2 border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                            />
                        </div>
                    </div>
                </div>
                <div className="bg-white rounded-xl shadow-lg overflow-hidden">
                    <div className="overflow-x-auto">
                        <table className="w-full text-left">
                            <thead className="bg-gray-50 border-b-2 border-gray-200">
                                <tr>
                                    <th className="p-4 text-sm font-semibold text-gray-600">ลิงก์</th>
                                    {isAdmin && viewMode === 'allLinks' && (
                                        <th className="p-4 text-sm font-semibold text-gray-600">ผู้สร้าง</th>
                                    )}
                                    <th className="p-4 text-sm font-semibold text-gray-600">รหัสผ่าน</th>
                                    <th className="p-4 text-sm font-semibold text-gray-600">ป้ายกำกับ</th>
                                    <th className="p-4 text-sm font-semibold text-gray-600">จำนวนคลิก</th>
                                    <th className="p-4 text-sm font-semibold text-gray-600">สถานะ</th>
                                    <th className="p-4 text-sm font-semibold text-gray-600">การดำเนินการ</th>
                                </tr>
                            </thead>
                            <tbody>
                                {currentUrls.map(url => {
                                    const baseUrl = window.location.origin;
                                    const shortUrl = `${baseUrl}/${url.shortCode}`;
                                    const isExpired = url.expiresAt && url.expiresAt.toDate() < new Date();
                                    const isMaxedOut = url.maxClicks > 0 && url.clickCount >= url.maxClicks;
                                    const isActive = !isExpired && !isMaxedOut;

                                    return (
                                        <tr key={url.id} className={`border-b border-gray-200 ${!isActive ? 'bg-gray-100 text-gray-500' : 'hover:bg-gray-50'}`}>
                                            <td className="p-4 align-top">
                                                <div className="flex items-center gap-2">
                                                    <a href={shortUrl} target="_blank" rel="noopener noreferrer" className={`font-semibold ${isActive ? 'text-blue-600 hover:underline' : ''}`}>
                                                        {shortUrl.replace(/^https?:\/\//, '')}
                                                    </a>
                                                    <button 
                                                        onClick={() => handleCopy(shortUrl)}
                                                        className="p-1 text-slate-400 hover:text-slate-700 rounded-full hover:bg-slate-100 transition-colors"
                                                        title="คัดลอกลิงก์"
                                                    >
                                                        <CopyIcon />
                                                    </button>
                                                    <ShareButton shortUrl={shortUrl} longUrl={url.longUrl} onCopy={handleCopy} />
                                                    {copySuccess === shortUrl && <span className="text-green-600 text-xs animate-pulse">คัดลอกแล้ว!</span>}
                                                </div>
                                                <p className="text-xs text-gray-500 break-all mt-1">{url.longUrl.substring(0, 50)}...</p>
                                            </td>
                                            {isAdmin && viewMode === 'allLinks' && (
                                                <td className="p-4 align-top text-sm text-gray-600 truncate" title={url.creatorEmail}>
                                                    {url.creatorEmail || 'N/A'}
                                                </td>
                                            )}
                                            <td className="p-4 align-top">
                                                {url.password ? (
                                                    visiblePasswords[url.id] ? (
                                                        <div className="flex items-center gap-2">
                                                            <span className="font-mono bg-gray-200 px-2 py-1 rounded text-sm">{url.password}</span>
                                                            <button onClick={() => togglePasswordVisibility(url.id)} className="text-sm text-blue-600 hover:underline">ซ่อน</button>
                                                        </div>
                                                    ) : (
                                                        <button onClick={() => togglePasswordVisibility(url.id)} className="text-sm text-blue-600 hover:underline flex items-center gap-1">
                                                            <EyeIcon /> ดู
                                                        </button>
                                                    )
                                                ) : (
                                                    <span className="text-gray-400">-</span>
                                                )}
                                            </td>
                                            <td className="p-4 align-top">
                                                <div className="flex flex-wrap gap-1">
                                                    {url.tags?.map(tag => (
                                                        <span key={tag} className="text-xs bg-gray-200 text-gray-800 px-2 py-1 rounded-full">{tag}</span>
                                                    ))}
                                                </div>
                                            </td>
                                            <td className="p-4 align-top font-medium">{url.clickCount || 0}{url.maxClicks > 0 ? ` / ${url.maxClicks}` : ''}</td>
                                            <td className="p-4 align-top">
                                                {isExpired ? <span className="text-red-500 font-bold">หมดอายุ</span> : isMaxedOut ? <span className="text-yellow-600 font-bold">คลิกเต็ม</span> : <span className="text-green-600 font-bold">ใช้งาน</span>}
                                            </td>
                                            <td className="p-4 align-top">
                                                <div className="flex items-center gap-1">
                                                    <button 
                                                        onClick={() => handleCustomUrlEditClick(url)}
                                                        className="p-2 text-slate-500 hover:bg-blue-100 hover:text-blue-700 rounded-full transition-colors"
                                                        title="แก้ไข Custom URL"
                                                    >
                                                        <AtSymbolIcon />
                                                    </button>
                                                    <button 
                                                        onClick={() => handleEditClick(url)}
                                                        className="p-2 text-slate-500 hover:bg-slate-200 hover:text-slate-700 rounded-full transition-colors"
                                                        title="แก้ไขลิงก์"
                                                    >
                                                        <EditIcon />
                                                    </button>
                                                    <Link to={`/details/${url.id}`} className="p-2 text-slate-500 hover:bg-slate-200 hover:text-slate-700 rounded-full transition-colors" title="ดูประวัติ">
                                                        <SearchIcon />
                                                    </Link>
                                                    <button 
                                                        onClick={() => handleDownloadQR(url.shortCode)}
                                                        className="p-2 text-slate-500 hover:bg-slate-200 hover:text-slate-700 rounded-full transition-colors"
                                                        title="ดาวน์โหลด QR Code"
                                                    >
                                                        <DownloadIcon />
                                                    </button>
                                                    <button 
                                                        onClick={() => handleDeleteClick(url)}
                                                        className="p-2 text-red-500 hover:bg-red-100 hover:text-red-700 rounded-full transition-colors"
                                                        title="ลบลิงก์"
                                                    >
                                                        <TrashIcon />
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                    {(currentPage > 1 || hasMorePages) && (
                        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center mt-4 p-4 border-t border-gray-200 gap-3">
                            <div className="text-sm text-gray-600 text-center sm:text-left">
                                หน้า {currentPage}
                            </div>

                            <button
                                onClick={handlePrevPage}
                                disabled={currentPage === 1}
                                className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                <ChevronLeftIcon />
                                <span className="hidden sm:inline">ก่อนหน้า</span>
                            </button>

                            <div className="flex items-center gap-1 overflow-x-auto">
                                {visiblePageNumbers.map((pageNumber) => (
                                    <button
                                        key={pageNumber}
                                        type="button"
                                        onClick={() => handleGotoPage(pageNumber)}
                                        disabled={isPageLoading || pageNumber === currentPage || (!(pageNumber in pageCursors) && pageNumber !== currentPage + 1)}
                                        className={`min-w-9 rounded-lg border px-3 py-2 text-sm font-semibold transition ${
                                            pageNumber === currentPage
                                                ? 'border-blue-600 bg-blue-600 text-white'
                                                : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-50'
                                        }`}
                                    >
                                        {pageNumber}
                                    </button>
                                ))}
                            </div>

                            <button
                                onClick={handleNextPage}
                                disabled={!hasMorePages}
                                className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                <span className="hidden sm:inline">ถัดไป</span>
                                <ChevronRightIcon />
                            </button>
                        </div>
                    )}
                </div>
            </div>
        </main>
    );
};
export default Dashboard;
